import * as React from 'react';
import isEmpty from 'lodash/isEmpty';

import OfferBannerContext from '../context/OfferBannerContext';
import OfferBannerDetails from '../details/OfferBannerDetails';
import OfferBannerRevokedQuote from './quote/OfferBannerRevokedQuote';

import { __t, applyTranslations } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { stripHTML } from 'utils/helper/common';
import { FUNC_RETURN_NULL } from '../utils/offer-banner';

import './offer-banner-revoked.scss';

applyTranslations(require('./offer-banner-revoked.i18n.json'));

import type { OfferBannerRevokedBy } from '../types';

type Props = {
  revokedBy: OfferBannerRevokedBy
};

const MSG_KEY = 'offer_banner_revoked';

const OfferBannerRevoked: React.FC<Props> = ({
  revokedBy
}: Props) => {
  const context = React.useContext(OfferBannerContext);
  const {
    userType,
    offer,
    updatedOffer,
    creatorProfile,
    brandProfileName,
    subcampaignExpirationType,
    isRenegotiation,
    isQuoteProvided,
    isBrand,
    renderGenericText = FUNC_RETURN_NULL
  } = context;

  const currentOffer = isRenegotiation ? updatedOffer : offer;
  const isQuote = currentOffer?.type === 'quote';

  const currentOfferLabelKey = currentOffer?.type === 'offer'
    ? 'new_budget_offer'
    : 'new_offer';

  const agreedOfferLabelKey = offer?.type === 'other'
    ? 'agreed_budget'
    : 'agreed_offer';

  const getDescriptionTextKeyCreator = (): string => {
    if (subcampaignExpirationType === 'expired' && !isQuoteProvided) {
      return 'offer_expired_creator';
    }

    if (subcampaignExpirationType === 'expired' && isQuoteProvided) {
      return 'offer_expired_brand';
    }

    return subcampaignExpirationType === 'campaign_ended'
      ? 'residual.stay_tuned'
      : 'residual.contact_brand';
  };

  const getDescriptionTextCreator = (): string => {
    const textKey = getDescriptionTextKeyCreator();

    return __t(`${MSG_KEY}.messages.creator.${textKey}`);
  };

  const getDescriptionTextKeyBrand = (): string => {
    if (revokedBy === 'brand' || revokedBy === 'influencer' || subcampaignExpirationType === 'campaign_ended') {
      return '';
    }

    if (!isQuoteProvided) {
      return 'offer_expired_creator';
    }

    if (isQuoteProvided) {
      return 'offer_expired_brand';
    }

    return '';
  };

  const getDescriptionTextBrand = (): string => {
    const textKey = getDescriptionTextKeyBrand();

    return isEmpty(textKey)
      ? textKey
      : __t(`${MSG_KEY}.messages.brand.${textKey}`);
  };

  const renderDescriptionText = (): React.ReactNode => {
    const text = isBrand
      ? getDescriptionTextBrand()
      : getDescriptionTextCreator();

    return renderGenericText(text);
  };

  const renderExpiredText = (): React.ReactNode => {
    let textKey = 'offer_expired';

    if (revokedBy === 'brand') {
      textKey = 'offer_canceled';
    }

    if (revokedBy === 'influencer') {
      textKey = 'offer_declined';
    }

    const text = __t(`${MSG_KEY}.messages.${userType}.${textKey}`, {
      creatorName: creatorProfile?.name,
      brandProfileName
    });

    return (
      <div
        className="offer-banner-revoked__expired-text"
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(text) }}
        title={stripHTML(text)}
      />
    );
  };

  if (isQuote) {
    return (
      <OfferBannerRevokedQuote
        offer={currentOffer}
        renderExpiredText={renderExpiredText}
        renderDescriptionText={renderDescriptionText}
      />
    );
  }

  return (
    <div className="offer-banner-revoked">
      <OfferBannerDetails
        offer={currentOffer}
        budgetLabel={__t(`${MSG_KEY}.labels.${currentOfferLabelKey}`)}
        textStyle="disabled"
        isCondensed
        showAdditionalCompensationDescriptions
      />
      {
        isRenegotiation && (
          <OfferBannerDetails
            offer={offer}
            budgetLabel={__t(`${MSG_KEY}.labels.${agreedOfferLabelKey}`)}
            textStyle="disabled"
          />
        )
      }
      {renderExpiredText()}
      {renderDescriptionText()}
    </div>
  );
};

export default OfferBannerRevoked;
